'use client';

import React from 'react';
import { addRpcUrlOverrideToChain, PrivyProvider } from '@privy-io/react-auth';
import { polygon } from 'viem/chains';

import { useTheme } from '@/contexts/ThemeContext';
import { colors } from '@/styles/theme';
import { POLYGON_RPC_URL } from '@/utils/approvals.utils';

interface PrivyAuthProviderProps {
  children: React.ReactNode;
}

export function PrivyAuthProvider({ children }: PrivyAuthProviderProps) {
  const { mode } = useTheme();
  const appId = process.env.NEXT_PUBLIC_PRIVY_APP_ID;
  const clientId = process.env.NEXT_PUBLIC_PRIVY_CLIENT_ID;

  if (!appId) {
    console.error('NEXT_PUBLIC_PRIVY_APP_ID is not defined');
    return <>{children}</>;
  }

  if (!clientId) {
    console.error('NEXT_PUBLIC_PRIVY_CLIENT_ID is not defined');
    return <>{children}</>;
  }

  return (
    <PrivyProvider
      appId={appId}
      clientId={clientId}
      config={{
        // NOTE: The login screen users actually see is the CUSTOM modal in
        // AuthModal.tsx (rendered by ModalsOrchestrator), NOT Privy's native modal.
        // The visible option order lives there in JSX. This `loginMethods` list only
        // gates which methods are enabled / used by Privy's native fallback flows
        // (e.g. the wallet-only `login({ loginMethods: ['wallet'] })` call), so
        // reordering it does not change what the user sees.
        // Keep this order in sync with AuthModal: Google → Apple → WhatsApp (sms) → Wallet → Email.
        loginMethods: ['google', 'apple', 'sms', 'wallet', 'email'],

        appearance: {
          theme: mode,
          accentColor: mode === 'dark' ? colors.primary.dark : colors.primary.light,
          logo: '/icon.svg',
          // NOTE: This is currently false even though the requirement comment says wallet-first.
          // Keep unchanged to avoid breaking current login UX; revisit with product decision.
          showWalletLoginFirst: false,
        },
        intl: {
          defaultCountry: 'US',
        },

        defaultChain: polygon,
        supportedChains: [addRpcUrlOverrideToChain(polygon, POLYGON_RPC_URL)],
        embeddedWallets: {
          ethereum: {
            // NOTE: With whitelabel/headless login, Privy does NOT auto-create the
            // embedded wallet from this flag — we create it manually in AuthInitializer.
            // Kept as a no-op safety net for any non-whitelabel path.
            createOnLogin: 'users-without-wallets',
          },
          // CRITICAL: sign without Privy's confirmation modal.
          // Right after login we run an automated multi-signature flow (proxy deploy →
          // token approvals → CLOB provisioning). When this is unset, Privy falls back to
          // the per-app Dashboard default; the new UAT Privy app defaults to showing the
          // wallet UI, so each background signature waits on a modal that never resolves
          // and the proxy setup hangs. Forcing `false` makes signing deterministic and
          // non-interactive across every environment/app.
          showWalletUIs: false,
        },
      }}
    >
      {children}
    </PrivyProvider>
  );
}

export default PrivyAuthProvider;
